import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthenticatedUser } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import {
    activityHeaders,
    createDwellTimer,
    describePage,
    flushActivity,
    recordPageView,
    setActivityEnabled,
    startPageView,
} from 'c-lib';

type Props = {
    // Every post-auth route template, so a path is recorded as its template, not its ids.
    routes: string[];
};

/**
 * Records a page view, with visible time on the page, each time a signed-in user leaves a page
 * (route change, tab closed, signed out), and turns `track()` on for them. Search params ride on
 * the page view as they stood when the user left, so filter changes don't count as new views.
 */
function ActivityTracker({ routes }: Props): null {
    const { pathname, search } = useLocation();
    const { user } = useAuthenticatedUser();
    const userId = user?.id;
    const searchRef = useRef(search);
    searchRef.current = search;

    // Declared before the effect that turns tracking off: React runs cleanups in declaration order,
    // so on sign-out or unmount the last page view is recorded before the queue is dropped.
    useEffect(() => {
        if (userId == null) return undefined;

        const page = describePage(pathname, routes);
        // Started here, before the page's own components fetch: this component renders ahead of
        // the routes, so its effects run first and their API calls already carry the id.
        const pageViewId = startPageView(page);
        const dwell = createDwellTimer();
        let recorded = false;

        const record = (unloading: boolean) => {
            if (recorded) return;
            recorded = true;
            recordPageView(page, dwell.elapsed(), searchRef.current, pageViewId);
            flushActivity(unloading);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                dwell.pause();
                // A hidden tab may never come back: send what is already queued while we can.
                flushActivity(true);
            } else {
                dwell.resume();
            }
        };
        const onPageHide = () => record(true);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            record(false);
        };
    }, [pathname, routes, userId]);

    useEffect(() => {
        setActivityEnabled(userId != null);
        return () => {
            flushActivity();
            setActivityEnabled(false);
        };
    }, [userId]);

    // Every API call says which page visit made it (`X-Activity-Page-View`, `X-Activity-Page`).
    useEffect(() => {
        const axios = ApiClient.HttpClient.Axios();
        const interceptor = axios.interceptors.request.use(config => {
            Object.entries(activityHeaders()).forEach(([name, value]) => {
                config.headers.set(name, value);
            });
            return config;
        });
        return () => axios.interceptors.request.eject(interceptor);
    }, []);

    return null;
}

export default ActivityTracker;
